import { toast } from 'sonner';

const TOAST_DURATION = 15000; // 15 seconds

/**
 * Centralized toast utilities for consistent error/success messaging.
 * Uses sonner under the hood. All toast calls should go through these helpers.
 */

/** Show a destructive/error toast. Pass onRetry to make "try again" clickable. */
export const showErrorToast = (message: string, title?: string, onRetry?: () => void) => {
  // Strip "Please try again." from description when we have a retry action
  const description = onRetry
    ? message.replace(/\.?\s*Please try again\.?/i, '').trim()
    : message;

  toast.error(title || 'Error', {
    description: description || message,
    duration: TOAST_DURATION,
    ...(onRetry && {
      action: {
        label: 'Try again',
        onClick: onRetry,
      },
    }),
  });
};

/** Show a success toast */
export const showSuccessToast = (message: string, title?: string) => {
  toast.success(title || 'Success', {
    description: message,
    duration: TOAST_DURATION,
  });
};

/** Show an informational toast */
export const showInfoToast = (message: string, title?: string) => {
  toast.info(title || 'Info', {
    description: message,
    duration: TOAST_DURATION,
  });
};

/** Show a warning toast */
export const showWarningToast = (message: string, title?: string) => {
  toast.warning(title || 'Warning', {
    description: message,
    duration: TOAST_DURATION,
  });
};

/**
 * Extract a user-friendly error message from an unknown error.
 * Use in catch blocks: showErrorToast(getErrorMessage(err))
 */
export const getErrorMessage = (err: unknown, fallback = 'An unexpected error occurred'): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};
